// THE PAGE TABS, IN THE HEADER (owner, 2026-09-28: "move the main tabs into a
// toggle-able navigation sidebar … Then, I suppose that we could basically move
// the sub-tabs into the spot of the current main tabs").
//
// The shell has three levels, and each has one place:
//
//   - the SIDEBAR picks the section (Invoices, Analytics, References, …);
//   - the HEADER, between the brand mark and the tools, picks the page within
//     it — a pipeline step on Invoices, a list on References, a tab on Rules;
//   - the ONE-LINE INTRO at the top of the body says what that page is for
//     (`ScreenIntro.tsx`).
//
// There are no page titles any more. The selected tab IS the page's name, which
// is why a section with one page still shows its one tab: take it away and
// nothing on screen says where you are.
//
// A PORTAL, the same move `ScreenActions.tsx` makes for the tool buttons: the
// tab state belongs to the screen that owns the content — `RulesScreen`'s tab
// decides which manager renders — so the screen renders this component and it
// draws itself into the header slot. `App.tsx` renders `PageTabsHost` once.
// ONE MOUNT AT A TIME is the caller's contract, as it is for `ScreenActions`.
//
// OVERFLOW. Tabs that do not fit fold into a trailing "more" menu rather than
// wrapping the header onto a second row. An Admin holds all ten pipeline steps,
// which is wider than the header on any laptop, and the sidebar being open
// takes 240px of the same width. The selected tab is always kept visible.
import { useLayoutEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Tab,
  TabList,
  makeStyles,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from '@fluentui/react-components'
import { MoreHorizontalRegular } from '@fluentui/react-icons'

const PAGE_TABS_ID = 'ip-page-tabs'

interface PageTab<V extends string = string> {
  value: V
  label: string
}

const useStyles = makeStyles({
  // The slot in the header. It takes whatever width the brand and the tools
  // leave, and `minWidth: 0` is what lets the tab strip shrink into its overflow
  // menu instead of pushing the tools off the right edge.
  host: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  // The strip FILLS the slot. The overflow measurement reads the strip's own
  // width, so a strip sized to its content measures only what it already
  // shows: it hid everything but the selected tab behind "…" while half the
  // header stood empty (seen in the browser, 2026-09-28).
  tabs: { flex: '1 1 0', minWidth: 0, overflow: 'hidden' },
  // A view switcher that rides the tab row (References → Codes, Rules →
  // Companies) keeps its full width; the tabs fold before it does. It sits at
  // the far end of the row, where it sat on the sub-tab row before the move.
  trailing: { flexShrink: 0, display: 'flex', alignItems: 'center' },
})

/** The empty slot in the header. Rendered once, by `App.tsx`. */
export function PageTabsHost() {
  const s = useStyles()
  return <div id={PAGE_TABS_ID} className={s.host} />
}

function OverflowMenuItem({ tab, onSelect }: { tab: PageTab; onSelect: (value: string) => void }) {
  const visible = useIsOverflowItemVisible(tab.value)
  if (visible) return null
  // Named explicitly (QA 2026-10-04, A-10), as the tabs are (A-08): the QA
  // tool's accessibility reader showed the item with no name.
  return (
    <MenuItem aria-label={tab.label} onClick={() => onSelect(tab.value)}>
      {tab.label}
    </MenuItem>
  )
}

function OverflowMenu({ tabs, onSelect }: { tabs: readonly PageTab[]; onSelect: (value: string) => void }) {
  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>()
  if (!isOverflowing) return null
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          ref={ref}
          appearance="transparent"
          icon={<MoreHorizontalRegular />}
          aria-label={`${overflowCount} more`}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {tabs.map((t) => (
            <OverflowMenuItem key={t.value} tab={t} onSelect={onSelect} />
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  )
}

/**
 * Draw a page's tabs into the header slot.
 *
 * The host is resolved in a layout effect, for the reason `ScreenActions`
 * gives: it is a sibling higher in the same tree and does not exist until the
 * commit that creates it.
 */
export function PageTabs<V extends string>({
  tabs,
  selectedValue,
  onSelect,
  ariaLabel,
  trailing,
}: {
  tabs: readonly PageTab<V>[]
  selectedValue: V | null
  onSelect: (value: V) => void
  ariaLabel: string
  /** A view switcher shown right after the tabs, on the same row. */
  trailing?: ReactNode
}) {
  const s = useStyles()
  const [host, setHost] = useState<HTMLElement | null>(null)
  useLayoutEffect(() => {
    setHost(document.getElementById(PAGE_TABS_ID))
  }, [])
  if (!host || tabs.length === 0) return null
  const select = (v: string) => onSelect(v as V)
  return createPortal(
    <>
      <Overflow minimumVisible={1}>
        <TabList
          className={s.tabs}
          selectedValue={selectedValue ?? undefined}
          onTabSelect={(_, d) => select(String(d.value))}
          aria-label={ariaLabel}
        >
          {tabs.map((t) => (
            <OverflowItem key={t.value} id={t.value} priority={t.value === selectedValue ? 2 : 1}>
              {/* Named explicitly (QA 2026-10-04, A-08): Fluent draws the
                  label twice, once hidden to reserve the bold width, and some
                  accessibility-tree readers then give the tab no name. */}
              <Tab value={t.value} aria-label={t.label}>
                {t.label}
              </Tab>
            </OverflowItem>
          ))}
          <OverflowMenu tabs={tabs} onSelect={select} />
        </TabList>
      </Overflow>
      {trailing && <div className={s.trailing}>{trailing}</div>}
    </>,
    host,
  )
}
